// frontend/packages/business-console/src/pages/BrandingSettings.jsx
//
// Workspace > Branding. The first self-serve surface for a tenant to set
// the logo + brand color that the quote / invoice / report renderers use
// (business.branding.logoAssetId + branding.primaryColor). Before this,
// those fields could only be set by the AI build interview, so existing
// tenants had no way to brand their documents.
//
// Logo selection reuses MediaPickerModal (the working upload/pick flow).
// Saving calls PATCH /businesses/branding, which ownership-checks the
// asset and audits the change server-side.

import React, { useContext, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

import { BusinessContext } from '../contexts/BusinessContext';
import { updateBusinessBranding } from '../services/businessService';
import MediaPickerModal from '../components/media/MediaPickerModal';
import { useTokens, PageHeader, Surface } from '../components/primitives';

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const DEFAULT_BRAND = '#2563eb';

function Panel({ title, subtitle, children, t }) {
  return (
    <Surface sx={{ overflow: 'hidden', mb: 2.25 }}>
      <Box sx={{ p: '17px 22px', borderBottom: `1px solid ${t.hairline}` }}>
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: t.text }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ fontSize: 12, color: t.muted, mt: '2px' }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ p: '20px 22px' }}>{children}</Box>
    </Surface>
  );
}

export default function BrandingSettings() {
  const t = useTokens();
  const { business, refreshBusiness } = useContext(BusinessContext);

  const initialColor = business?.branding?.primaryColor || '';
  const hasExistingLogo = Boolean(business?.branding?.logoAssetId);

  const [color, setColor] = useState(initialColor);
  // When the user picks a new logo we hold its id + url locally; the id is
  // what we persist, the url is for the live preview.
  const [pickedLogo, setPickedLogo] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const colorValid = color === '' || HEX_RE.test(color.trim());
  const swatch = colorValid && color ? color.trim() : DEFAULT_BRAND;

  const previewLogoUrl = pickedLogo?.url || null;

  const onPick = useMemo(
    () => async (asset) => {
      if (asset?.id) {
        setPickedLogo({ assetId: asset.id, url: asset.publicUrl || null });
        setSuccess('');
      }
      setPickerOpen(false);
    },
    [],
  );

  const onSave = async () => {
    setError('');
    setSuccess('');
    if (!colorValid) {
      setError('Brand color must be a hex value like #2563eb.');
      return;
    }
    setSaving(true);
    try {
      const resp = await updateBusinessBranding({
        // Keep the existing logo if the user didn't pick a new one.
        logoAssetId: pickedLogo
          ? pickedLogo.assetId
          : business?.branding?.logoAssetId || null,
        primaryColor: color.trim() || '',
      });
      await refreshBusiness?.();
      // If the tenant has a published website, the save also updated their
      // site draft (server-side branding-sync). Tell them, and that a publish
      // is what makes it live, so the change is discoverable.
      const synced = resp?.data?.siteSync?.synced === true;
      setSuccess(
        synced
          ? 'Branding saved. Your website draft was updated too. Publish your site to make the new branding live. New estimates and invoices use it right away.'
          : 'Branding saved. New estimates and invoices will use it.',
      );
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || 'Failed to save branding.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Branding"
        sx={{ mb: 2.5 }}
        meta={
          <Typography sx={{ fontSize: 13, color: t.muted }}>
            Your logo and brand color appear on the estimates, invoices, and
            reports you send.
          </Typography>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      <Panel
        title="Logo"
        subtitle="Shown at the top of your documents. Pick an image from your media library."
        t={t}
      >
        <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap">
          <Box
            sx={{
              width: 180,
              height: 72,
              border: `1px dashed ${t.hairline}`,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: t.subtle || 'transparent',
              overflow: 'hidden',
            }}
          >
            {previewLogoUrl ? (
              <Box
                component="img"
                src={previewLogoUrl}
                alt="Logo preview"
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : hasExistingLogo ? (
              <Typography sx={{ fontSize: 12, color: t.muted }}>
                Current logo set
              </Typography>
            ) : (
              <ImageOutlinedIcon sx={{ color: t.muted }} />
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<ImageOutlinedIcon />}
            onClick={() => setPickerOpen(true)}
          >
            {hasExistingLogo || pickedLogo ? 'Change logo' : 'Choose logo'}
          </Button>
        </Stack>
      </Panel>

      <Panel
        title="Brand color"
        subtitle="Used for headings and accents on your documents."
        t={t}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: swatch,
              border: `1px solid ${t.hairline}`,
              flex: '0 0 auto',
            }}
          />
          <TextField
            size="small"
            label="Hex color"
            placeholder="#2563eb"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            error={!colorValid}
            helperText={!colorValid ? 'Use a hex value like #2563eb' : ' '}
            sx={{ width: 200 }}
          />
        </Stack>
      </Panel>

      <Box sx={{ mt: 1 }}>
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save branding'}
        </Button>
      </Box>

      <MediaPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={onPick}
      />
    </Box>
  );
}
